"use client";
import { memo, useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import ModalVideo from "react-modal-video";
import "react-modal-video/css/modal-video.css";
import { FaPlay } from "react-icons/fa";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { useTranslation } from "next-i18next";
// api
import { getCloudfrontUrl } from "../utils/mediaHelper";

const CaseStudyVideos = memo(({ casestudyvideos, className = "" }) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const lang = router.locale || "en";
  const [isOpen, setOpen] = useState(false);
  const [videoId, setVideoId] = useState(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [navigation, setNavigation] = useState({ prevEl: null, nextEl: null });

  useEffect(() => {
    if (prevRef.current && nextRef.current) {
      setNavigation({
        prevEl: prevRef.current,
        nextEl: nextRef.current,
      });
    }
  }, [prevRef.current, nextRef.current]); // safe in modern React

  const extractYouTubeID = (url) => {
    const regex =
      /(?:youtube\.com\/(?:shorts\/|[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const openVideoModal = (videoUrl) => {
    // setVideoId(videoUrl);
    // setOpen(true);
    const id = extractYouTubeID(videoUrl);
    if (id) {
      setVideoId(id);
      setOpen(true);
    }
  };

  return (
    <div
      className={`w-full box-border px-[24px] md:px-[60px] lg:px-[80px] xl:px-[80px] pb-[40px] md:pb-[40px] lg:pb-[60px] lg:pb-[60px] max-w-[1600px] mx-auto ${className}`}
    >
      {/* Header */}
      <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-6 md:mb-10">
        <h2 className="text-[28px] sm:text-[32px] lg:text-[40px] font-medium leading-[120%] m-0">
          {t("caseStudy.title")}
        </h2>
        {lang === "ar" ? (
          <div className="flex gap-3">
            <button
              ref={nextRef}
              className="cursor-pointer w-[44px] h-[44px] border border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition"
            >
              <IoIosArrowForward size={20} />
            </button>
            <button
              ref={prevRef}
              className="cursor-pointer w-[44px] h-[44px] border border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition"
            >
              <IoIosArrowBack size={20} />
            </button>
          </div>
        ) : (
          <div className="flex gap-3">
            <button
              ref={prevRef}
              className="cursor-pointer w-[44px] h-[44px] border border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition"
            >
              <IoIosArrowBack size={20} />
            </button>
            <button
              ref={nextRef}
              className="cursor-pointer w-[44px] h-[44px] border border-black rounded-full flex items-center justify-center text-black bg-transparent hover:bg-black hover:text-white transition"
            >
              <IoIosArrowForward size={20} />
            </button>
          </div>
        )}
      </div>

      {/* Carousel */}

      <Swiper
        dir={lang === "ar" && "ltr"}
        modules={[Navigation]}
        spaceBetween={40}
        navigation={navigation}
        onBeforeInit={(swiper) => {
          if (typeof swiper.params.navigation !== "boolean") {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }
        }}
        breakpoints={{
          320: { slidesPerView: 1 },
          640: { slidesPerView: 2 },
          1024: { slidesPerView: 2 },
          1280: { slidesPerView: 2 },
        }}
      >
        {casestudyvideos?.map((caseVideo) => (
          <SwiperSlide key={caseVideo?._id}>
            <div
              onClick={() => openVideoModal(caseVideo?.youtubeUrl)}
              // className="rounded-2xl overflow-hidden border border-gray-300 border-solid p-3 bg-black"
            >
              <div className="relative w-full max-w-[640px] rounded-[24px] overflow-hidden">
                <Image
                  src={getCloudfrontUrl(caseVideo?.thumbnailUrl)}
                  // alt={caseVideo?.name}
                  alt={caseVideo?.altText}
                  width={640}
                  height={200}
                  className="w-full h-auto object-contain"
                />
                {caseVideo?.youtubeUrl && (
                  <div className="absolute inset-0 flex items-center justify-center ">
                    <div className="bg-black/60 rounded-full flex items-center justify-center cursor-pointer">
                      <FaPlay className="text-white text-[24px] p-3" />
                    </div>
                  </div>
                )}
              </div>
              {/* <div className="py-4 text-white text-[16px]">
                  <p className="mb-2">
                    “Horem ipsum dolor sit amet, consectetur adipiscing elit.
                    Nunc vulputate libero et velit interdum, ac aliquet odio
                    mattis.”
                  </p>
                  <p className="font-medium m-0">{testimonial?.name}</p>
                </div> */}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* YouTube Modal */}
      <ModalVideo
        channel="youtube"
        videoId={videoId}
        // channel="custom"
        // url={videoId}
        isOpen={isOpen}
        onClose={() => setOpen(false)}
      />
    </div>
  );
});

CaseStudyVideos.propTypes = {
  className: PropTypes.string,
};

export default CaseStudyVideos;
